import Styles from './setup.module.scss';
import React, { useState, useCallback, useEffect } from 'react';
import {
    Typography,
    TextField,
    Autocomplete,
    Grid as MuiGrid,
    Dialog
} from '@mui/material';
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';
import axios from 'axios';
import codes from 'country-calling-code';
import { OtpVerification } from 'views/auth/signin/otpVerify';
import CaptchaField from 'components/global/CaptchaField/CaptchaField';

export const ContactVerificationForm = ({ onSuccess, userData, context, t, authContext }) => {
    // Determine verification type based on missing data
    const verificationType = !userData?.phone_number ? 'phone' : !userData?.email ? 'email' : null;
    const isEmailType = verificationType === 'email';

    // Form state
    const [contactValue, setContactValue] = useState(
        verificationType === 'phone' ? userData?.phone_number || '' : userData?.email || ''
    );
    const [countryCode, setCountryCode] = useState('91');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // OTP dialog state
    const [dialogOpen, setDialogOpen] = useState(false);
    const [OTP, setOTP] = useState('');

    //Captcha state
    const [captcha, setCaptcha] = useState({
        image: "",
        id: "",
        input: ""
    })

    //useEffects
    useEffect(() => {
        if (!dialogOpen) {
            refreshCaptcha();
        }
    }, [dialogOpen])

    const validateInput = useCallback((value) => {
        if (isEmailType) {
            const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return re.test(value) ? '' : 'Enter a valid email';
        } else {
            const re = /^\d{6,14}$/;
            return re.test(value) ? '' : 'Enter a valid phone number';
        }
    }, [isEmailType]);

    const validCaptcha = (captcha) => {
        const re = /^-?\d+$/;
        return re.test(captcha)
    }

    const handleChange = useCallback((e) => {
        const { value } = e.target;
        setContactValue(value);
        setError(validateInput(value));
    }, [validateInput]);

    const refreshCaptcha = async () => {
        try {
            const res = await axios.get("/api/auth/captcha");
            setCaptcha({
                image: res.data.image,
                id: res.data.captchaId,
                input: "",
            });
        } catch (err) {
            context.notification.show("Failed to load captcha. Please try again later", "error", true);
        }
    }

    const handleSendOTP = async () => {
        const validationError = validateInput(contactValue);
        if (validationError) {
            setError(validationError);
            return;
        }

        setLoading(true);
        const queryParams = new URLSearchParams({
            [isEmailType ? 'email' : 'mobile']: true,
            set_up: true,
        }).toString();

        let options = {
            method: 'POST',
            url: `/api/auth/otp?${queryParams}`,
            data: isEmailType
                ? { email: contactValue, first_name: userData?.first_name || 'User', captcha_id: captcha.id, captcha_input: captcha.input }
                : { mobile_number: `+${countryCode} ${contactValue}`, captcha_id: captcha.id, captcha_input: captcha.input }
        }

        axios
            .request(options)
            .then((response) => {
                if ((response.data.otp_sent.email_otp === true) || (response.data.otp_sent.mobile_otp === true)) {
                    context.notification.show(response.data.message, 'success', true);
                    setDialogOpen(true);
                    setOTP('');
                } else {
                    context.notification.show(response.data.message, 'error');
                }
            })
            .catch((error) => {
                const data = error.response?.data
                const message = data?.message || data;
                if (['CAPTCHA_INVALID', 'CAPTCHA_EXPIRED'].includes(data?.errorCode) && data?.captcha) {
                    setCaptcha({ id: data.captcha.captchaId, image: data.captcha.image, input: '' })
                }
                context.notification.show(message, 'error');
            })
            .finally(() => {
                setLoading(false)
            })
    };

    const handleResendOTP = async () => {
        setLoading(true);
        const queryParams = new URLSearchParams({
            [isEmailType ? 'email' : 'mobile']: true
        }).toString();

        let options = {
            method: 'POST',
            url: `/api/auth/otp/resend?${queryParams}`,
            data: isEmailType
                ? { email: contactValue, first_name: userData?.first_name || 'User' }
                : { mobile_number: `+${countryCode} ${contactValue}` }
        }

        axios
            .request(options)
            .then((response) => {
                if ((response.data.otp_sent.email_otp === true) || (response.data.otp_sent.mobile_otp === true)) {
                    context.notification.show(response.data.message, 'success', true);
                } else {
                    context.notification.show(response.data.message, 'error');
                }
            })
            .catch((error) => {
                const data = error.response?.data
                const message = data?.message || data;
                if (['CAPTCHA_INVALID', 'CAPTCHA_EXPIRED'].includes(data?.errorCode) && data?.captcha) {
                    setCaptcha({ id: data.captcha.captchaId, image: data.captcha.image, input: '' })
                }
                context.notification.show(message, 'error');
                if (dialogOpen) {
                    setDialogOpen(false);
                    setOTP('')
                }
            })
            .finally(() => {
                setLoading(false);
            })
    };

    const handleVerifyOTP = async () => {
        setLoading(true);

        const options = {
            method: 'POST',
            url: '/api/auth/otp/verify',
            data: {
                ...(isEmailType
                    ? {
                        email_otp: OTP,
                        email_verification_required: true,
                        email: contactValue
                    }
                    : {
                        mobile_otp: OTP,
                        mobile_verification_required: true,
                        mobile_number: contactValue,
                        country_code: countryCode
                    }),
            },
        };
        axios
            .request(options)
            .then(function (response) {
                authContext.update(isEmailType ? { email: contactValue } : { phone_number: contactValue, country_code: countryCode })
                onSuccess(isEmailType ? { email: contactValue } : { phone: contactValue });
                context.notification.show("Verification successful", 'success', true);
            })
            .catch((error) => {
                const data = error.response?.data
                const message = data?.message || data;
                if (['CAPTCHA_INVALID', 'CAPTCHA_EXPIRED'].includes(data?.errorCode) && data?.captcha) {
                    setCaptcha({ id: data.captcha.captchaId, image: data.captcha.image, input: '' })
                }
                context.notification.show(message, 'error');
                if (dialogOpen) {
                    setOTP('')
                }
            })
            .finally(() => {
                setLoading(false);
                setDialogOpen(false);
            });
    };

    const handleCaptchaInput = (input) => {
        setCaptcha((prev) => ({ ...prev, input: input }));
    }

    return (
        <>
            <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: '1em', color: '#1450a0' }}>
                {`Verify Your Contact Information`}
            </Typography>

            <div className={Styles.formContainer}>
                {isEmailType ? (
                    <TextField
                        label="Email"
                        type="email"
                        value={contactValue}
                        onChange={handleChange}
                        margin="normal"
                        size="small"
                        error={!!error}
                        helperText={error}
                        placeholder="johndoe@example.com"
                        disabled={loading}
                        fullWidth
                        slotProps={{
                            input: {
                                style: {
                                    height: "50px",
                                }
                            }
                        }}

                    />
                ) : (
                    <>
                        <MuiGrid container spacing={2}>
                            <MuiGrid item xs={4}>
                                <Autocomplete
                                    options={codes}
                                    getOptionLabel={(option) => `${option.isoCode3} +${option.countryCodes[0]}`}
                                    value={codes.find(option => option.countryCodes[0] === countryCode) || null}
                                    onChange={(event, newValue) => {
                                        setCountryCode(newValue.countryCodes[0]);
                                    }}
                                    disableClearable
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Country"
                                            name="countryCode"
                                            required
                                            sx={{
                                                '& .MuiInputBase-root': {
                                                    height: '50px',
                                                }
                                            }}
                                        />
                                    )}
                                />
                            </MuiGrid>
                            <MuiGrid item xs={8}>
                                <TextField
                                    label="Phone Number"
                                    name="phoneNumber"
                                    type="tel"
                                    value={contactValue}
                                    onChange={handleChange}
                                    placeholder="1234567890"
                                    error={!!error}
                                    helperText={error}
                                    disabled={loading}
                                    required
                                    fullWidth
                                    slotProps={{
                                        input: {
                                            style: {
                                                height: "50px",
                                            }
                                        }
                                    }}
                                />
                            </MuiGrid>
                        </MuiGrid>
                    </>
                )}
                <Typography variant='subtitle2' color='text.secondary' sx={{ fontStyle: 'italic', marginTop: '0.5em' }} >
                    We'll send a verification code to confirm your contact information
                </Typography>
                {/* Captcha */}
                {!!captcha?.image && (
                    <CaptchaField
                        captcha={captcha}
                        onCaptchaChange={handleCaptchaInput}
                        onRefreshCaptcha={refreshCaptcha}
                        error={!validCaptcha(captcha.input?.trim())}
                        helperText={
                            !validCaptcha(captcha.input) ? "Enter the result" : ""
                        }
                    />
                )}
            </div>

            <div id='sendVerificationButton' className={Styles.buttonContainer}>
                <AivagamButton
                    buttonText={loading ? "Sending..." : "Send Verification Code"}
                    type="button"
                    disabled={
                        loading ||
                        !contactValue ||
                        !!error ||
                        !validCaptcha(captcha.input) ||
                        (!isEmailType && countryCode.length === 0)
                    }
                    classStyles={{
                        fontSize: '1em',
                        height: '3em',
                        padding: "0.5em 2em",
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
                    }}
                    onClick={handleSendOTP}
                />
            </div>

            {/* OTP Verification Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={() => {/* Prevent closing by clicking outside */ }}
                fullWidth
            >
                <OtpVerification
                    OTP={OTP}
                    setOTP={setOTP}
                    onVerify={handleVerifyOTP}
                    onResend={handleResendOTP}
                    onSubmitNewOtp={handleSendOTP}
                    onEdit={() => { }}
                    signUpMethod={verificationType}
                    editable={false}
                    destination={contactValue}
                    loading={loading}
                    t={t}
                />
            </Dialog>
        </>
    );
};
